// src/components/Home/BenefitCard.jsx
import React from "react";
import { Card, Button, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import StarRatingBadge from "../../components/Rating/StarRating";
import "./BenefitCard.css";

const isTrue = (v) => v === 1 || v === "1" || v === true;

const BenefitCard = ({
  benefit,
  comercio,
  onClaim,
  showBusinessLink = true,
  actions,
  typeLabel,
  distanceLabel,
}) => {
  const token = localStorage.getItem("token");
  const now = new Date();
  const validUntil = benefit.valid_until ? new Date(benefit.valid_until) : null;

  const expiredByFlag =
    typeof benefit.is_expired !== "undefined" ? isTrue(benefit.is_expired) : false;
  const expiredByDate = validUntil ? validUntil < now : false;
  const expired = expiredByFlag || expiredByDate;

  const status = benefit.status;
  const statusActive =
    status === 1 ||
    status === "1" ||
    status === "active" ||
    status === "ACTIVE";

  const active = statusActive && !expired;

  const formattedDate =
    validUntil && !isNaN(validUntil)
      ? validUntil.toLocaleDateString("es-AR", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : null;

  const numericPrice = benefit.price != null ? Number(benefit.price) : null;
  const hasPrice = numericPrice != null && !isNaN(numericPrice) && numericPrice > 0;

  const discountType =
    benefit.discount_type || (benefit.discount_percentage ? "percentage" : null);

  const discountPercentage =
    benefit.discount_percentage != null ? Number(benefit.discount_percentage) : null;

  const discountAmount =
    benefit.discount_amount != null ? Number(benefit.discount_amount) : null;

  const basePrice = hasPrice ? numericPrice : null;

  const computeFinalPrice = () => {
    if (!basePrice || !discountType) return null;

    if (discountType === "percentage" && discountPercentage != null) {
      return Math.max(basePrice * (1 - discountPercentage / 100), 0);
    }

    if (discountType === "fixed" && discountAmount != null) {
      return Math.max(basePrice - discountAmount, 0);
    }

    if (discountType === "two_for_one") return null;

    return null;
  };

  const finalPrice = computeFinalPrice();

  const formatCurrency = (value) =>
    value.toLocaleString("es-AR", {
      style: "currency",
      currency: "ARS",
    });

  const renderDiscountBadge = () => {
    if (expired || !discountType) return null;

    if (discountType === "percentage" && discountPercentage != null) {
      return <Badge className="benefit-badge-discount">-{discountPercentage}% OFF</Badge>;
    }

    if (discountType === "fixed" && discountAmount != null) {
      return (
        <Badge className="benefit-badge-discount">
          Ahorras {formatCurrency(discountAmount)}
        </Badge>
      );
    }

    if (discountType === "two_for_one") {
      return <Badge className="benefit-badge-discount">2x1</Badge>;
    }

    return null;
  };

  const renderPriceBlock = () => {
    if (!hasPrice) return null;

    const hasRealDiscount = discountType && finalPrice !== null;

    return (
      <div className="benefit-price-block">
        <small className="benefit-price-label">
          {hasRealDiscount ? "Precio con descuento" : "Precio"}
        </small>

        {hasRealDiscount ? (
          <>
            <strong className="benefit-price-final">{formatCurrency(finalPrice)}</strong>
            <span className="benefit-price-base">Antes {formatCurrency(basePrice)}</span>
          </>
        ) : (
          <strong className="benefit-price-final">{formatCurrency(basePrice)}</strong>
        )}

        {discountType === "two_for_one" && (
          <small className="benefit-price-note">2x1 sobre este precio</small>
        )}
      </div>
    );
  };

  const actionLabel = !active
    ? "No disponible"
    : benefit.claimed
    ? "Ya reclamado"
    : token
    ? "Reclamar"
    : "Iniciar sesion";
  const hasMetaBadges = Boolean(benefit.category || typeLabel || distanceLabel);

  return (
    <Card className="benefit-card h-100">
      <div className="benefit-media">
        <Card.Img
          variant="top"
          src={benefit.image_url || "/default-product.jpg"}
          alt={benefit.name}
          className="benefit-image"
          loading="lazy"
        />

        <StarRatingBadge benefitId={benefit.id} placement="br" />

        <div className="benefit-media-top-left">{renderDiscountBadge()}</div>

        <div className="benefit-media-top-right">
          {expired && <Badge bg="secondary">Vencido</Badge>}
          {benefit._isUsed && !expired && <Badge bg="dark">Usado</Badge>}
        </div>
      </div>

      <Card.Body className="benefit-card-body d-flex flex-column">
        <Card.Title className="benefit-title">{benefit.name}</Card.Title>

        {showBusinessLink && comercio?.name && comercio?.slug && (
          <div className="mb-2">
            <Link to={`/catalogo/${comercio.slug}`} className="benefit-business-link">
              {comercio.name}
            </Link>
          </div>
        )}

        {benefit.description && (
          <Card.Text className="benefit-description">
            {benefit.description.length > 110
              ? `${benefit.description.slice(0, 110)}...`
              : benefit.description}
          </Card.Text>
        )}

        {hasMetaBadges && (
          <div className="benefit-meta-row">
            {benefit.category && <Badge className="benefit-badge-category">{benefit.category}</Badge>}
            {typeLabel && <Badge className="benefit-badge-type">{typeLabel}</Badge>}
            {distanceLabel && <Badge bg="secondary">{distanceLabel}</Badge>}
          </div>
        )}

        <div className="mt-auto">
          {renderPriceBlock()}

          {actions ? (
            <div className="mt-2">{actions}</div>
          ) : (
            <Button
              variant="primary"
              className="w-100 benefit-cta"
              disabled={!active || benefit.claimed}
              onClick={() => onClaim && onClaim(benefit.id)}
            >
              {actionLabel}
            </Button>
          )}

          <small className="benefit-validity">
            {formattedDate ? (expired ? "Expirado" : `Vence: ${formattedDate}`) : "Sin vencimiento"}
          </small>
        </div>
      </Card.Body>
    </Card>
  );
};

export default BenefitCard;
